import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, CreditCard, Star, CheckCircle } from 'lucide-react';

const BuyCreditsPage: React.FC = () => {
  const handleComingSoon = () => {
    alert('Payment integration coming soon! We\'ll add Stripe payment options in the next update.');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex justify-between items-center">
            <Link 
              to="/profile"
              className="flex items-center space-x-2 text-primary-600 hover:text-primary-700"
            >
              <ArrowLeft className="h-4 w-4" />
              <span>Back to Profile</span>
            </Link>
            <Link
              to="/generator"
              className="text-primary-600 hover:text-primary-700 font-medium"
            >
              Generator
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">Buy Credits</h1>
          <p className="text-xl text-gray-600">Choose a credit package that fits your needs</p>
        </div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
          {/* Starter Pack */}
          <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
            <div className="text-center mb-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Starter Pack</h3>
              <div className="text-3xl font-bold text-primary-600 mb-1">$9</div>
              <p className="text-gray-600">25 Credits</p>
            </div>
            
            <ul className="space-y-3 mb-6">
              <li className="flex items-center space-x-2">
                <CheckCircle className="h-4 w-4 text-green-500" />
                <span className="text-gray-700">25 email generations</span>
              </li>
              <li className="flex items-center space-x-2">
                <CheckCircle className="h-4 w-4 text-green-500" />
                <span className="text-gray-700">LinkedIn profile targeting</span>
              </li>
              <li className="flex items-center space-x-2">
                <CheckCircle className="h-4 w-4 text-green-500" />
                <span className="text-gray-700">Company news integration</span>
              </li>
              <li className="flex items-center space-x-2">
                <CheckCircle className="h-4 w-4 text-green-500" />
                <span className="text-gray-700">All tones & services</span>
              </li>
            </ul>
            
            <button
              onClick={handleComingSoon}
              className="w-full bg-gray-100 hover:bg-gray-200 text-gray-900 py-3 px-4 rounded-lg font-medium transition-colors"
            >
              Select Plan
            </button>
          </div>

          {/* Popular Pack */}
          <div className="bg-white rounded-xl shadow-lg p-6 border-2 border-primary-500 relative">
            <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
              <div className="bg-primary-500 text-white px-4 py-1 rounded-full text-sm font-medium flex items-center space-x-1">
                <Star className="h-3 w-3" />
                <span>Most Popular</span>
              </div>
            </div>
            
            <div className="text-center mb-6 mt-4">
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Professional Pack</h3>
              <div className="text-3xl font-bold text-primary-600 mb-1">$19</div>
              <p className="text-gray-600">60 Credits</p>
              <p className="text-xs text-green-600 font-medium">Save 20%</p>
            </div>
            
            <ul className="space-y-3 mb-6">
              <li className="flex items-center space-x-2">
                <CheckCircle className="h-4 w-4 text-green-500" />
                <span className="text-gray-700">60 email generations</span>
              </li>
              <li className="flex items-center space-x-2">
                <CheckCircle className="h-4 w-4 text-green-500" />
                <span className="text-gray-700">LinkedIn profile targeting</span>
              </li>
              <li className="flex items-center space-x-2">
                <CheckCircle className="h-4 w-4 text-green-500" />
                <span className="text-gray-700">Company news integration</span>
              </li>
              <li className="flex items-center space-x-2">
                <CheckCircle className="h-4 w-4 text-green-500" />
                <span className="text-gray-700">All tones & services</span>
              </li>
              <li className="flex items-center space-x-2">
                <CheckCircle className="h-4 w-4 text-green-500" />
                <span className="text-gray-700">Priority support</span>
              </li>
            </ul>
            
            <button
              onClick={handleComingSoon}
              className="w-full bg-primary-600 hover:bg-primary-700 text-white py-3 px-4 rounded-lg font-medium transition-colors"
            >
              Select Plan
            </button>
          </div>

          {/* Enterprise Pack */}
          <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
            <div className="text-center mb-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Enterprise Pack</h3>
              <div className="text-3xl font-bold text-primary-600 mb-1">$39</div>
              <p className="text-gray-600">150 Credits</p>
              <p className="text-xs text-green-600 font-medium">Save 30%</p>
            </div>
            
            <ul className="space-y-3 mb-6">
              <li className="flex items-center space-x-2">
                <CheckCircle className="h-4 w-4 text-green-500" />
                <span className="text-gray-700">150 email generations</span>
              </li>
              <li className="flex items-center space-x-2">
                <CheckCircle className="h-4 w-4 text-green-500" />
                <span className="text-gray-700">LinkedIn profile targeting</span>
              </li>
              <li className="flex items-center space-x-2">
                <CheckCircle className="h-4 w-4 text-green-500" />
                <span className="text-gray-700">Company news integration</span>
              </li>
              <li className="flex items-center space-x-2">
                <CheckCircle className="h-4 w-4 text-green-500" />
                <span className="text-gray-700">All tones & services</span>
              </li>
              <li className="flex items-center space-x-2">
                <CheckCircle className="h-4 w-4 text-green-500" />
                <span className="text-gray-700">Priority support</span>
              </li>
              <li className="flex items-center space-x-2">
                <CheckCircle className="h-4 w-4 text-green-500" />
                <span className="text-gray-700">Custom templates (coming soon)</span>
              </li>
            </ul>
            
            <button
              onClick={handleComingSoon}
              className="w-full bg-gray-100 hover:bg-gray-200 text-gray-900 py-3 px-4 rounded-lg font-medium transition-colors"
            >
              Select Plan
            </button>
          </div>
        </div>

        {/* Coming Soon Notice */}
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
          <CreditCard className="h-12 w-12 text-yellow-600 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-yellow-900 mb-2">Payment Integration Coming Soon!</h3>
          <p className="text-yellow-800 mb-4">
            We're working hard to bring you secure payment options. In the meantime, enjoy your free credits!
          </p>
          <p className="text-sm text-yellow-700">
            Expected launch: Next update cycle. We'll notify all users via email when it's ready.
          </p>
        </div>

        {/* FAQ Section */}
        <div className="mt-12">
          <h2 className="text-2xl font-bold text-gray-900 text-center mb-8">Frequently Asked Questions</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white rounded-lg p-6 shadow">
              <h4 className="font-semibold text-gray-900 mb-2">How do credits work?</h4>
              <p className="text-gray-600">Each successful email generation uses 1 credit. Credits never expire.</p>
            </div>
            
            <div className="bg-white rounded-lg p-6 shadow">
              <h4 className="font-semibold text-gray-900 mb-2">What if the generation fails?</h4>
              <p className="text-gray-600">No credits are deducted if the email generation fails due to technical issues.</p>
            </div>
            
            <div className="bg-white rounded-lg p-6 shadow">
              <h4 className="font-semibold text-gray-900 mb-2">Can I get a refund?</h4>
              <p className="text-gray-600">We offer a 7-day money-back guarantee for all credit purchases.</p>
            </div>
            
            <div className="bg-white rounded-lg p-6 shadow">
              <h4 className="font-semibold text-gray-900 mb-2">Do you offer bulk discounts?</h4>
              <p className="text-gray-600">Yes! Contact us for custom pricing on 500+ credit packages.</p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default BuyCreditsPage;